import SnackbarUtils from 'Util/Snackbar'
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { t } from 'ttag'

export enum CopyObjectKeys {
  SERVICE_PROVIDER = 'serviceProvider',
  SALES_POINT = 'salesPoint',
  DEVICE = 'device'
}

type CopyObjectStore = Record<string, unknown>

interface CopyObjectContextValue {
  /**
   * Copy an object into the context under the given key.
   * Optionally omit fields listed in `ignoredFields`.
   * If the same key already exists, the old data is replaced.
   */
  copyObject: <T extends object>(key: string, obj: T, ignoredFields?: (keyof T)[]) => void

  /**
   * Retrieve the copied object stored under the given key.
   * Returns `null` if nothing is stored for that key.
   */
  getCopiedObject: <T>(key: string) => T | null

  /**
   * Remove a specific key from the store without retrieving it.
   */
  clearCopiedObject: (key: string) => void
}

const CopyObjectContext = createContext<CopyObjectContextValue | undefined>(undefined)

export const CopyObjectProvider = ({ children }: { children: ReactNode }) => {
  const [store, setStore] = useState<CopyObjectStore>({})

  const copyObject = useCallback(
    <T extends object>(key: string, obj: T, ignoredFields: (keyof T)[] = []) => {
      const objectCopy: Partial<T> = { ...obj }
      for (const field of ignoredFields) {
        delete objectCopy[field]
      }
      setStore(prev => ({ ...prev, [key]: objectCopy }))
      SnackbarUtils.info(t`Object data copied to template.`)
    },
    []
  )

  const getCopiedObject = useCallback(
    <T,>(key: string): T | null => {
      const data = store[key]
      if (data === undefined) return null
      return data as T
    },
    [store]
  )

  const clearCopiedObject = useCallback((key: string) => {
    setStore(prev => {
      const { [key]: _, ...rest } = prev
      return rest
    })
  }, [])

  const contextValue = useMemo(
    () => ({ copyObject, getCopiedObject, clearCopiedObject }),
    [copyObject, getCopiedObject, clearCopiedObject]
  )

  return <CopyObjectContext.Provider value={contextValue}>{children}</CopyObjectContext.Provider>
}

export const useCopyObjectContext = (): CopyObjectContextValue => {
  const context = useContext(CopyObjectContext)
  if (!context) {
    throw new Error('useCopyObjectContext must be used within a CopyObjectProvider')
  }
  return context
}
